/**
 * MessageContent — Rich Markdown Rendering for Chat Messages
 *
 * Uses react-markdown + remark-gfm for robust markdown parsing.
 * Custom components preserve Trendy's table export toolbar,
 * email draft card, and photo gallery features.
 */

import { useState, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Copy, Check, Download, FileSpreadsheet, FileText, ClipboardCopy, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { copyTableToClipboard, downloadCSV, downloadExcel, downloadPDF } from '../../lib/tableExport'
import { linkifyEntities, isEntityHubUrl } from '../../lib/entityLinks'
import AttachmentGallery, { parseGalleryData } from './AttachmentGallery'
import InlineVisual, { parseInlineVisuals, tryParseInlineVisual } from './InlineVisuals'
import DOMPurify from 'dompurify'

// ── Stable constants — prevent react-markdown from seeing new arrays every render ──
const REMARK_PLUGINS = [remarkGfm]

/**
 * Extract a balanced JSON object from a string starting at a given position.
 * Handles nested braces, strings with escaped characters, and arrays.
 */
export function extractBalancedJson(str, startIndex) {
  if (str[startIndex] !== '{') return null
  let depth = 0
  let inString = false
  let escape = false
  for (let i = startIndex; i < str.length; i++) {
    const ch = str[i]
    if (escape) { escape = false; continue }
    if (ch === '\\' && inString) { escape = true; continue }
    if (ch === '"' && !escape) { inString = !inString; continue }
    if (inString) continue
    if (ch === '{' || ch === '[') depth++
    if (ch === '}' || ch === ']') depth--
    if (depth === 0) return str.substring(startIndex, i + 1)
  }
  return null
}

/**
 * Detect if content contains HTML tags
 */
function containsHtml(content) {
  const htmlPattern = /<(p|div|span|strong|em|br|ul|ol|li|h[1-6]|a|table|tr|td|th|blockquote)\b[^>]*>/i
  return htmlPattern.test(content)
}

/**
 * Convert HTML to markdown-like text for display
 */
function htmlToReadableText(html) {
  const temp = document.createElement('div')
  temp.innerHTML = DOMPurify.sanitize(html)

  temp.querySelectorAll('br').forEach(br => br.replaceWith('\n'))
  temp.querySelectorAll('p').forEach(p => p.insertAdjacentText('afterend', '\n\n'))
  temp.querySelectorAll('strong, b').forEach(el => el.replaceWith(`**${el.textContent}**`))
  temp.querySelectorAll('em, i').forEach(el => el.replaceWith(`*${el.textContent}*`))
  temp.querySelectorAll('li').forEach(li => {
    li.insertAdjacentText('beforebegin', '- ')
    li.insertAdjacentText('afterend', '\n')
  })

  return temp.innerHTML
    .replace(/&nbsp;/g, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Strip markdown for clean export data
 */
function stripMarkdown(text) {
  if (!text) return text
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
}

/**
 * Table Export Toolbar — Copy, CSV, Excel, PDF
 */
function TableExportToolbar({ tableRef }) {
  const [copied, setCopied] = useState(false)
  const [exporting, setExporting] = useState(null)

  // Extract headers and rows from the rendered table DOM
  const getTableData = useCallback(() => {
    const table = tableRef?.current
    if (!table) return { headers: [], rows: [] }

    const headers = Array.from(table.querySelectorAll('thead th'))
      .map(th => stripMarkdown(th.textContent?.trim() || ''))
    const rows = Array.from(table.querySelectorAll('tbody tr'))
      .map(tr =>
        Array.from(tr.querySelectorAll('td'))
          .map(td => stripMarkdown(td.textContent?.trim() || ''))
      )
    return { headers, rows }
  }, [tableRef])

  const handleCopy = async () => {
    const { headers, rows } = getTableData()
    await copyTableToClipboard(headers, rows)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleCSV = () => { const { headers, rows } = getTableData(); downloadCSV(headers, rows) }

  const handleExcel = async () => {
    setExporting('excel')
    try { const { headers, rows } = getTableData(); await downloadExcel(headers, rows) }
    catch (err) { console.error('Excel export failed:', err) }
    finally { setExporting(null) }
  }

  const handlePDF = async () => {
    setExporting('pdf')
    try { const { headers, rows } = getTableData(); await downloadPDF(headers, rows) }
    catch (err) { console.error('PDF export failed:', err) }
    finally { setExporting(null) }
  }

  const btnClass = "inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-surface-dark-overlay rounded transition-colors"

  return (
    <div className="flex items-center gap-1 mb-1">
      <button onClick={handleCopy} className={btnClass} title="Copy to clipboard">
        {copied ? <Check size={13} className="text-green-600" /> : <ClipboardCopy size={13} />}
        <span>{copied ? 'Copied' : 'Copy'}</span>
      </button>
      <button onClick={handleCSV} className={btnClass} title="Download as CSV">
        <Download size={13} /><span>CSV</span>
      </button>
      <button onClick={handleExcel} className={btnClass} title="Download as Excel">
        {exporting === 'excel'
          ? <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin" />
          : <FileSpreadsheet size={13} />}
        <span>Excel</span>
      </button>
      <button onClick={handlePDF} className={btnClass} title="Download as PDF">
        {exporting === 'pdf'
          ? <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin" />
          : <FileText size={13} />}
        <span>PDF</span>
      </button>
    </div>
  )
}

/**
 * Wrapper for tables that adds the export toolbar
 */
function TableWithExport({ children, ...props }) {
  const tableRef = { current: null }

  return (
    <div>
      <TableExportToolbar tableRef={tableRef} />
      <div className="table-wrapper">
        <table ref={(el) => { tableRef.current = el }} {...props}>
          {children}
        </table>
      </div>
    </div>
  )
}

/* ─── Declarative Layout Renderer ─────────────────────────────────────────── */

/**
 * Try to parse a layout JSON block from a string.
 * Returns the parsed object if it matches {"type": "layout", ...}, else null.
 */
function tryParseLayoutJson(text) {
  if (!text) return null
  const str = String(text).trim()
  if (!str.startsWith('{')) return null
  try {
    const parsed = JSON.parse(str)
    if (parsed && parsed.type === 'layout' && Array.isArray(parsed.sections)) {
      return parsed
    }
  } catch {
    // not valid JSON
  }
  return null
}

/**
 * Parse content for layout blocks embedded in code fences.
 * Returns { layout, beforeLayout, afterLayout } or null.
 */
function parseLayoutBlock(content) {
  if (!content) return null
  // Match ```json ... ``` or ``` ... ``` containing layout JSON
  const codeBlockRegex = /```(?:json)?\s*\n?([\s\S]*?)\n?```/g
  let match
  while ((match = codeBlockRegex.exec(content)) !== null) {
    const layout = tryParseLayoutJson(match[1])
    if (layout) {
      const beforeLayout = content.substring(0, match.index).trim()
      const afterLayout = content.substring(match.index + match[0].length).trim()
      return { layout, beforeLayout, afterLayout }
    }
  }
  return null
}

/** Trend icon for KPI items */
function TrendIcon({ trend }) {
  if (trend === 'up') return <TrendingUp size={14} className="text-emerald-500" />
  if (trend === 'down') return <TrendingDown size={14} className="text-red-500" />
  return <Minus size={14} className="text-gray-400" />
}

/** Color mapping for metric bars */
const BAR_COLORS = {
  blue: 'bg-blue-500',
  green: 'bg-emerald-500',
  red: 'bg-red-500',
  yellow: 'bg-amber-500',
  purple: 'bg-purple-500',
  orange: 'bg-orange-500',
}

/**
 * DeclarativeLayout — renders a rich card from a layout JSON spec.
 * Supports section types: kpi_row, table, list, metric_bar.
 */
function DeclarativeLayout({ data }) {
  if (!data || !data.sections) return null

  return (
    <div className="my-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark overflow-hidden shadow-sm">
      {data.title && (
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{data.title}</h3>
        </div>
      )}
      <div className="p-5 space-y-5">
        {data.sections.map((section, i) => (
          <LayoutSection key={i} section={section} />
        ))}
      </div>
    </div>
  )
}

function LayoutSection({ section }) {
  switch (section.type) {
    case 'kpi_row':
      return <KpiRow items={section.items || []} />
    case 'table':
      return <LayoutTable title={section.title} headers={section.headers || []} rows={section.rows || []} />
    case 'list':
      return <LayoutList title={section.title} items={section.items || []} />
    case 'metric_bar':
      return <MetricBar label={section.label} value={section.value} max={section.max || 100} color={section.color || 'blue'} />
    default:
      return null
  }
}

function KpiRow({ items }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {items.map((item, i) => (
        <div key={i} className="rounded-lg border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-surface-dark-overlay p-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{item.label}</p>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{item.value}</p>
          {(item.trend || item.change) && (
            <div className="flex items-center gap-1 mt-1">
              {item.trend && <TrendIcon trend={item.trend} />}
              {item.change && (
                <span className={`text-xs font-medium ${
                  item.trend === 'up' ? 'text-emerald-600' : item.trend === 'down' ? 'text-red-600' : 'text-gray-500'
                }`}>
                  {item.change}
                </span>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function LayoutTable({ title, headers, rows }) {
  return (
    <div>
      {title && <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{title}</p>}
      <div className="overflow-x-auto rounded-lg border border-gray-100 dark:border-gray-700">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-surface-dark-overlay">
              {headers.map((h, i) => (
                <th key={i} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {rows.map((row, ri) => (
              <tr key={ri} className="hover:bg-gray-50 dark:hover:bg-surface-dark-overlay transition-colors">
                {(Array.isArray(row) ? row : [row]).map((cell, ci) => (
                  <td key={ci} className="px-3 py-2 text-gray-700 dark:text-gray-300">{String(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function LayoutList({ title, items }) {
  return (
    <div>
      {title && <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{title}</p>}
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}

function MetricBar({ label, value, max, color }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100))
  const barColor = BAR_COLORS[color] || BAR_COLORS.blue

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{value}/{max}</span>
      </div>
      <div className="w-full h-2.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

/**
 * Code block with copy button
 */
function CodeBlock({ children, className }) {
  const [copied, setCopied] = useState(false)
  const match = /language-(\w+)/.exec(className || '')
  const language = match ? match[1] : undefined
  const isBlock = !!className?.startsWith('language-')

  if (isBlock) {
    const codeStr = String(children).replace(/\n$/, '')

    const handleCopy = () => {
      navigator.clipboard.writeText(codeStr)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }

    return (
      <code className={className || ''}>
        {children}
        <button
          className="code-copy-btn"
          onClick={handleCopy}
          title={copied ? 'Copied!' : 'Copy code'}
        >
          {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
        </button>
      </code>
    )
  }

  // Inline code
  return <code className="inline-code">{children}</code>
}


/**
 * Main MessageContent component
 */
export default function MessageContent({ content, onSendMessage }) {
  // Check for special content types
  const galleryData = parseGalleryData(content)

  // Convert HTML to markdown if needed, then linkify Epicor entity refs
  // ("Customer #247", "Job 25712", etc.) into clickable hub URLs. Skips
  // matches inside code fences / existing links, so this is safe to run
  // unconditionally on every assistant response.
  const processedContent = useMemo(() => {
    const cleaned = containsHtml(content) ? htmlToReadableText(content) : content
    return linkifyEntities(cleaned)
  }, [content])

  /**
   * Split content into segments of text and inline visuals.
   * Uses a simple, reliable approach:
   *   1. Find every occurrence of {"type":"inline" (with or without code fences)
   *   2. Use balanced brace matching to extract the full JSON
   *   3. Also capture any surrounding code fence markers for clean stripping
   *   4. Split content into text/visual segments
   */
  const contentSegments = useMemo(() => {
    const text = processedContent
    if (!text || !text.includes('"inline"')) return null

    const matches = []

    // Find every {"type":"inline" or { "type" : "inline" pattern
    const finder = /\{\s*"type"\s*:\s*"inline"/g
    let hit
    while ((hit = finder.exec(text)) !== null) {
      // Extract balanced JSON from this position
      const jsonStr = extractBalancedJson(text, hit.index)
      if (!jsonStr) continue

      try {
        const parsed = JSON.parse(jsonStr)
        if (!parsed || parsed.type !== 'inline' || !parsed.vizType) continue

        // Determine the full range to strip (including surrounding code fences if present)
        let stripStart = hit.index
        let stripEnd = hit.index + jsonStr.length

        // Look backwards for code fence opening: ```json or ```
        const beforeChunk = text.substring(Math.max(0, hit.index - 30), hit.index)
        const fenceOpenMatch = beforeChunk.match(/```(?:json)?\s*\n?\s*$/)
        if (fenceOpenMatch) {
          stripStart = hit.index - fenceOpenMatch[0].length
        }

        // Look forwards for code fence closing: ```
        const afterChunk = text.substring(stripEnd, Math.min(text.length, stripEnd + 20))
        const fenceCloseMatch = afterChunk.match(/^\s*\n?\s*```/)
        if (fenceCloseMatch) {
          stripEnd = stripEnd + fenceCloseMatch[0].length
        }

        matches.push({ start: stripStart, end: stripEnd, data: parsed })
      } catch { /* not valid JSON, skip */ }
    }

    if (matches.length === 0) return null

    // Build segments from matches
    const segments = []
    let cursor = 0
    for (const m of matches) {
      const before = text.substring(cursor, m.start).trim()
      if (before) segments.push({ type: 'text', content: before })
      segments.push({ type: 'visual', data: m.data })
      cursor = m.end
    }
    const after = text.substring(cursor).trim()
    if (after) segments.push({ type: 'text', content: after })

    return segments.length > 0 ? segments : null
  }, [processedContent])

  // Custom react-markdown component overrides (memoized for stable ref)
  const markdownComponents = useMemo(() => ({
    code: CodeBlock,

    pre({ children, ...props }) {
      // Intercept code blocks that contain layout JSON — render as DeclarativeLayout instead
      const codeChild = children?.props?.children
      if (codeChild) {
        const codeStr = String(codeChild).replace(/\n$/, '')
        const layout = tryParseLayoutJson(codeStr)
        if (layout) return <DeclarativeLayout data={layout} />
        // Intercept inline visual JSON — render as InlineVisual
        const inlineViz = tryParseInlineVisual(codeStr)
        if (inlineViz) return <InlineVisual data={inlineViz} />
      }
      return <pre className="code-block" {...props}>{children}</pre>
    },

    table({ children, ...props }) {
      return <TableWithExport {...props}>{children}</TableWithExport>
    },

    // Anchor override: entity-hub links go through React Router so we
    // do not full-reload the SPA. Everything else (external URLs, hash
    // anchors, mailto, etc.) renders as a normal anchor with safe rel.
    a({ href, children, ...props }) {
      if (isEntityHubUrl(href)) {
        return (
          <Link
            to={href}
            className="text-amtrend-blue underline decoration-amtrend-blue/30 underline-offset-2 hover:decoration-amtrend-blue transition-colors"
          >
            {children}
          </Link>
        )
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      )
    },
  }), [])

  // Photo Gallery rendering
  if (galleryData && galleryData.for_display) {
    const jsonStart = content.indexOf('{"type":"photo_gallery"') !== -1
      ? content.indexOf('{"type":"photo_gallery"')
      : content.indexOf('{ "type": "photo_gallery"')
    const beforeGallery = jsonStart > 0 ? content.substring(0, jsonStart).trim() : ''

    return (
      <div className="message-content">
        {beforeGallery && (
          <div className="markdown-body">
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={markdownComponents}>
              {beforeGallery}
            </ReactMarkdown>
          </div>
        )}
        <AttachmentGallery
          galleryData={galleryData}
          onEmailRequest={(message) => onSendMessage?.(message)}
        />
      </div>
    )
  }

  // (Legacy EmailDraftCard inline rendering removed 2026-05-23 — emails are now
  // emitted as `email_compose` artifacts in JSON code blocks and rendered in the
  // side panel. See backend/app/chat.py Email Protocol.)

  // Declarative layout rendering — detect layout JSON in code fences
  const layoutBlock = useMemo(() => parseLayoutBlock(processedContent), [processedContent])

  if (layoutBlock) {
    return (
      <div className="message-content">
        {layoutBlock.beforeLayout && (
          <div className="markdown-body">
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={markdownComponents}>
              {layoutBlock.beforeLayout}
            </ReactMarkdown>
          </div>
        )}
        <DeclarativeLayout data={layoutBlock.layout} />
        {layoutBlock.afterLayout && (
          <div className="markdown-body">
            <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={markdownComponents}>
              {layoutBlock.afterLayout}
            </ReactMarkdown>
          </div>
        )}
      </div>
    )
  }

  // Segment-based rendering — interleaves text and inline visuals
  if (contentSegments) {
    return (
      <div className="message-content">
        {contentSegments.map((seg, i) => {
          if (seg.type === 'visual') {
            return <InlineVisual key={`viz-${i}`} data={seg.data} />
          }
          return (
            <div key={`text-${i}`} className="markdown-body">
              <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={markdownComponents}>
                {seg.content}
              </ReactMarkdown>
            </div>
          )
        })}
      </div>
    )
  }

  // Regular markdown rendering (no inline visuals detected)
  return (
    <div className="message-content">
      <div className="markdown-body">
        <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={markdownComponents}>
          {processedContent}
        </ReactMarkdown>
      </div>
    </div>
  )
}
